import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CourseCardType } from '../../enums/courseCardType.enum';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material/material.module';
import { Course } from '../../models/course';
import { CourseEnrollment } from '../../models/courseEnrollment';
import { Router } from '@angular/router';
import { PipesModule } from '../../pipes/pipes.module';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { isNullOrUndefined } from '@pineapplelab/util';
import { ToasterService } from '../../services/toaster/toaster.service';
import { ToasterTypeEnum } from '../../enums/toasterType.enum';
import { ThreeDotsActionsEnum } from '../../enums/threeDotsActions.enum';
import { ThreeDotsComponent } from '../three-dots/three-dots.component';
import { CourseService } from '../../services/course/course.service';
import { AuthService } from '../../services/auth/auth.service';
import { FavoriteService } from '../../services/favorite/favorite.service';
import { FavoriteTypeEnum } from '../../enums/favoriteType.enum';
import { Favorite } from '../../models/favorite';
import { PagingComponent } from '../paging/paging.component';
import { RouteEnum } from '../../enums/routes.enum';
import { CategoryEnrollment } from '../../models/categoryEnrollment';
import { GenericAlertPopupComponent } from '../generic-alert-popup/generic-alert-popup.component';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TooltipDirective } from '../../directives/tooltip/tooltip.directive';
import { SectionsEnum } from '../../enums/sections.enum';
import { ImageComponent } from '../image/image.component';
import { Category } from '../../models/category';
import { IMAGES_RESOLUTIONS } from '../../utils/image-config';
import { DynamicImageDirective } from '../../directives/dynamic-images/dynamic-image.directive';

@Component({
	selector: 'app-course-card',
	standalone: true,
	imports: [CommonModule, MaterialModule, PipesModule, ThreeDotsComponent, TooltipDirective, ImageComponent, DynamicImageDirective ],
	templateUrl: './course-card.component.html',
	styleUrl: './course-card.component.scss'
})
export class CourseCardComponent implements OnChanges {

	@Input() cardType!: CourseCardType;
	@Input() cardTypeOrientation!: CourseCardType;
	@Input() courseEnrollment: CourseEnrollment;
	@Input() categoryEnrollment: CategoryEnrollment;
	@Input() category: Category;
	@Input() categoryName: string = null;
	@Input() categoryId: string = null;
	@Input() course: Course;
	@Input() favorite: Favorite;
	@Input() canCompleteForm: boolean = false;
	@Input() canAccessCourse: boolean = true;
	@Input() accessDeniedKey: string = 'general.cant_complete_form';
	@Input() overviewCourse: boolean = false;

	@Output() action = new EventEmitter<ThreeDotsActionsEnum>();
	@Output() updateView = new EventEmitter<boolean>(false);

	@ViewChild(PagingComponent) pagingComponent: PagingComponent;
	@ViewChild(ThreeDotsComponent) threeDotsComponent: ThreeDotsComponent;

	cardTypes = CourseCardType;
	PublishStatusEnum = PublishStatusEnum;
	completedChapters: number = 0;
	threeDotsActionsEnum = ThreeDotsActionsEnum;
	FavoriteTypeEnum = FavoriteTypeEnum;
	RESOLUTIONS = IMAGES_RESOLUTIONS;

	private router = inject(Router);
	private toasterService = inject(ToasterService);
	private courseService = inject(CourseService);
	private authService = inject(AuthService);
	private favoriteService = inject(FavoriteService);
	private dialog = inject(MatDialog);
	private cdRef = inject(ChangeDetectorRef);

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['course'] ) {
			this.updateThreeDotsComponent();
		}
	}

	private updateThreeDotsComponent() {
		if (this.threeDotsComponent) {
		  this.threeDotsComponent.initializeOptions();
		  this.cdRef.detectChanges();
		}
	}

	ngOnInit(): void {
		if (!isNullOrUndefined(this.courseEnrollment)) {
			this.completedChapters = this.courseEnrollment.__completedChapters
				?? (this.courseEnrollment.chapterEnrollments ?? []).filter(chapter => chapter?.completedAt).length;
		}
	}

	goToCourse() {
		if (!this.canAccessCourse) return;
		if (this.cardType == CourseCardType.FAVORITE) {
			let coursePath = `my-courses/chapter-view/${this.favorite.categoryId}/${this.favorite.courseId}`;
			if (this.favorite.type == FavoriteTypeEnum.COURSE) {
				this.router.navigate([coursePath]);
			} else if (this.favorite.type == FavoriteTypeEnum.CHAPTER) {
				this.router.navigate([coursePath], { queryParams: { chapterIndex: this.favorite.chapterIndex } });
			}
		}else{
			this.router.navigate([`my-courses/chapter-view/${this.categoryId}/${this.courseEnrollment?.id}`]);
		}
	}

	async markAsFavorite(e: Event) {
		e.stopPropagation();
		let marked = this.courseEnrollment.isFavorite;
		await this.favoriteService.markOrUnmark(this.courseEnrollment, this.categoryId, this.categoryName, this.authService.auth.user, this.authService.projectId, !marked, FavoriteTypeEnum.COURSE);
		this.courseEnrollment.isFavorite = !marked;
	}

	async unmarkFavorite(e: Event) {
		await this.favoriteService.unmarkExistent(this.favorite, this.authService.auth.user, this.authService.projectId);
		this.updateView.emit(true);
	}

	copyId(e: Event, courseId: string) {
		e.stopPropagation();
		navigator.clipboard.writeText(courseId)
		this.toasterService.show('general.copy_id', ToasterTypeEnum.SUCCESS);
	}

	async handleAction(ev: ThreeDotsActionsEnum) {
		switch (ev) {
			case this.threeDotsActionsEnum.EDIT:
				this.router.navigateByUrl(`/${RouteEnum.COURSE_CREATION}/${this.course?.id}`);
				break;
			case this.threeDotsActionsEnum.DELETE:
				this.deleteCourse();
				break;
			case this.threeDotsActionsEnum.PUBLISH:
				this.publishCourse();
				break;
			case this.threeDotsActionsEnum.DUPLICATE:
				this.duplicateModel();
				break;
			case this.threeDotsActionsEnum.ADD_TRANSLATIONS:
				this.router.navigate([`/course-creation/${this.course?.id}`], { queryParams: { courseSection: SectionsEnum.TRANSLATIONS, isAddTranslation: true } });
			break;
		}
	}

	async publishCourse(){
		await this.courseService.publish(this.course, this.authService.projectId);
		this.updateView.emit(true)
		this.toasterService.show('general.course_published_successfully', ToasterTypeEnum.SUCCESS);
	}

	async deleteCourse(): Promise<void> {
		const dialog = this.dialog.open(GenericAlertPopupComponent, {
			width: '500px',
			height: '253px',
			data:
			{
				title: 'general.delete_course',
				description: 'general.delete_course_warning',
				acceptBtnText: 'general.delete',
				btnColor: 'danger'
			},
			disableClose: true
		});
		const dialogData = await firstValueFrom(dialog.afterClosed());
		if (dialogData) {
			await this.courseService.delete(this.course, this.authService.projectId);
			this.toasterService.show('general.course_deleted_successfully', ToasterTypeEnum.SUCCESS);
			this.updateView.emit(true);
		}

	}

	async duplicateModel(): Promise<void> {
		await this.courseService.duplicate(this.course, this.authService.projectId, this.authService.auth?.user?.role);
		this.toasterService.show('general.course_duplicated_successfully', ToasterTypeEnum.SUCCESS, null, false, { course: this.course?.name });
		this.updateView.emit(true);
	}

	goToForm(){
		const requiredFirst = this.categoryEnrollment?.__assignedCategory?.requireFormFirst;
		this.router.navigate([`${RouteEnum.MY_COURSES}/${this.categoryId}/jotForm`], { queryParams: { requiredFirst } });
	}

	goCourseDetails(model: Course){
		this.router.navigate([`${RouteEnum.MY_COURSES}/${this.categoryId}/${RouteEnum.COURSE_DETAILS}`], { queryParams: { courseId: model.id } });
	}

	addToCart(model: Category){
		this.router.navigate([`${RouteEnum.CATEGORY_OVERVIEW}`], { queryParams: { categoryId: model.id } });
	}
}
